import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { clientApiService } from '@/services/api/client.service';

export type BotType = 'chatbot' | 'voicebot';

export interface ClientBotTypeResult {
  botType: BotType;
  isVoicebot: boolean;
  isChatbot: boolean;
  isLoading: boolean;
  clientProjectId?: string;
  clientDetails?: {
    id: string;
    name: string;
    vapi_tag: string;
    project_type: 'chatbot' | 'voicebot';
    status: 'active' | 'inactive';
    stats: {
      lead_count: number;
      conversation_count: number;
    };
  } | null;
}

/**
 * Hook para determinar el tipo de bot basado en el client_project_id del usuario
 * Consulta el endpoint /clients/:id para obtener el project_type y detalles completos
 * SE EJECUTA INMEDIATAMENTE después del login para evitar delays en el routing
 */
export const useClientBotType = (): ClientBotTypeResult => {
  const { user, isAuthenticated } = useAuth();
  
  const { data: clientDetails, isLoading } = useQuery({
    queryKey: ['client-details', user?.client_project_id],
    queryFn: async () => {
      if (!user?.client_project_id) {
        console.warn('useClientBotType: No client_project_id found for user');
        return null;
      }
      
      try {
        const details = await clientApiService.getClientDetails(user.client_project_id);
        console.log('useClientBotType: Client details loaded:', details);
        return details;
      } catch (error) {
        console.error('useClientBotType: Failed to load client details:', error);
        throw error;
      }
    },
    enabled: isAuthenticated && !!user?.client_project_id,
    staleTime: 10 * 60 * 1000, // Cache por 10 minutos
    cacheTime: 15 * 60 * 1000, // Mantener en cache por 15 minutos
    retry: 2, // Reintentar 2 veces si falla
    refetchOnMount: false, // No refetch en mount si ya está cacheado
    refetchOnWindowFocus: false, // No refetch en focus
    refetchOnReconnect: false, // No refetch al reconectar
  });
  
  // Extraer project_type de la respuesta del API (estructura actualizada)
  const botType: BotType = clientDetails?.project_type || 'chatbot';
  
  return {
    botType,
    isVoicebot: botType === 'voicebot',
    isChatbot: botType === 'chatbot',
    isLoading,
    clientProjectId: user?.client_project_id,
    clientDetails
  };
};

/**
 * Función utilitaria para determinar si un proyecto es de tipo chatbot
 */
export const isChatbot = (projectType: string): projectType is 'chatbot' => {
  return projectType === 'chatbot';
};

/**
 * Función utilitaria para determinar si un proyecto es de tipo voicebot
 */
export const isVoicebot = (projectType: string): projectType is 'voicebot' => {
  return projectType === 'voicebot';
};